import type { Middleware } from "openapi-fetch";
import createClient from "openapi-fetch";
import type { paths } from "../deferred-api/contracts";
import {
  assertRequestAuthority,
  captureRequestAuthority,
  requestAuthorityHeader,
} from "./authority";
import {
  type MutationIntent,
  MutationIntents,
  mutationFingerprint,
  mutationRequestScope,
} from "./mutation-intents";
import { currentCyraSessionId, readSessionCredentials } from "./session-credentials";
import { createInteractionId } from "./session-headers";

const inFlightIntents = new Map<
  string,
  { intent: MutationIntent; fence: () => void; partnerCreate: boolean }
>();
const requestFences = new WeakMap<Request, () => void>();

const authMiddleware: Middleware = {
  async onRequest({ request, id }) {
    const authority = captureRequestAuthority();
    const initiatingRevision = request.headers.get(requestAuthorityHeader);
    request.headers.delete(requestAuthorityHeader);
    if (initiatingRevision !== null && initiatingRevision !== String(authority.revision))
      throw new DOMException("Authority changed during request preparation.", "AbortError");
    const initialSessionId = request.headers.get("x-cyra-session-id") ?? currentCyraSessionId();
    assertRequestAuthority(authority, initialSessionId);
    requestFences.set(request, () => {
      request.signal.throwIfAborted();
      const sessionId = currentCyraSessionId();
      assertRequestAuthority(authority, sessionId);
      if (initialSessionId !== sessionId)
        throw new DOMException("Session changed before request send.", "AbortError");
    });
    if (!request.headers.has("x-cyra-interaction-id")) {
      request.headers.set("x-cyra-interaction-id", createInteractionId());
    }
    const credentials = await readSessionCredentials();
    request.signal.throwIfAborted();
    assertRequestAuthority(authority, credentials?.sessionId ?? null);
    if (initialSessionId !== (credentials?.sessionId ?? null))
      throw new DOMException("Session changed during request preparation.", "AbortError");
    if (!credentials) return request;
    request.headers.set("Authorization", `Bearer ${credentials.firebaseToken}`);
    request.headers.set("x-cyra-installation-reference", credentials.installationReference);
    request.headers.set("x-cyra-session-id", credentials.sessionId);
    const proposedKey = request.headers.get("idempotency-key");
    if (proposedKey) {
      if (!authority.actorId)
        throw new DOMException(
          "Verified actor required before preparing a mutation.",
          "AbortError",
        );
      const fingerprint = await mutationFingerprint(authority.actorId, request);
      const scope = await mutationRequestScope(authority.actorId, request);
      request.signal.throwIfAborted();
      assertRequestAuthority(authority, currentCyraSessionId());
      if (initialSessionId !== currentCyraSessionId())
        throw new DOMException("Session changed during request preparation.", "AbortError");
      const intent = new MutationIntents(window.sessionStorage).reserve(
        fingerprint,
        proposedKey,
        scope.operationFingerprint,
        scope.sourceFingerprint,
        scope.conflictingScopes,
      );
      request.headers.set("idempotency-key", intent.key);
      inFlightIntents.set(id, {
        intent,
        partnerCreate: Boolean(scope.ownedCreate),
        fence: () => assertRequestAuthority(authority, currentCyraSessionId()),
      });
    }
    return request;
  },
  async onResponse({ response, request, id }) {
    const attempt = inFlightIntents.get(id);
    if (attempt) {
      const intents = new MutationIntents(window.sessionStorage);
      try {
        attempt.fence();
        // Headers alone are not a completed response: a broken response body is uncertain.
        const text = await response.clone().text();
        const body = response.headers.get("content-type")?.includes("application/json")
          ? JSON.parse(text)
          : undefined;
        attempt.fence();
        if (response.ok && new URL(request.url).pathname === "/v1/admin/catalogue-upload") {
          if (
            typeof body?.complete !== "boolean" ||
            !Array.isArray(body.rows) ||
            !body.rows.length ||
            body.rows.length > 100 ||
            body.rows.some(
              (row: Record<string, unknown>, index: number) =>
                !row ||
                !Number.isInteger(row["row"]) ||
                row["row"] !== index + 2 ||
                !["CREATED", "REJECTED", "UNCONFIRMED", "UNPROCESSED"].includes(
                  String(row["state"]),
                ) ||
                typeof row["message"] !== "string" ||
                !Array.isArray(row["fields"]) ||
                row["fields"].some(
                  (field: Record<string, unknown>) =>
                    !field ||
                    typeof field["field"] !== "string" ||
                    typeof field["message"] !== "string",
                ) ||
                (body.complete && !["CREATED", "REJECTED"].includes(String(row["state"]))) ||
                (row["state"] !== "CREATED" && (row["id"] !== null || row["version"] !== null)) ||
                (row["state"] === "CREATED" &&
                  (typeof row["id"] !== "string" ||
                    !row["id"] ||
                    !Number.isInteger(row["version"]) ||
                    Number(row["version"]) < 1)),
            )
          )
            throw Error("Upload receipt could not be verified. Reconcile this same file.");
          if (body.complete) intents.complete(attempt.intent);
          else intents.uncertain(attempt.intent);
        } else if (response.ok) {
          if (attempt.partnerCreate) {
            const { assertApiMutationResult } = await import("./owned-mutation-result");
            assertApiMutationResult(body, request);
            attempt.fence();
          }
          intents.complete(attempt.intent);
        } else if (body?.error?.mutationDisposition === "NOT_APPLIED")
          intents.notApplied(attempt.intent);
        else intents.uncertain(attempt.intent);
      } catch (error) {
        intents.uncertain(attempt.intent);
        throw error;
      } finally {
        inFlightIntents.delete(id);
      }
    }
    return response;
  },
  onError({ id }) {
    // Keep the durable intent; a network failure cannot establish whether the server committed.
    const attempt = inFlightIntents.get(id);
    if (attempt) new MutationIntents(window.sessionStorage).uncertain(attempt.intent);
    inFlightIntents.delete(id);
  },
};

export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000";
export const apiClient = createClient<paths>({
  baseUrl: apiBaseUrl,
  fetch: (request) => {
    // openapi-fetch awaits middleware results. Fence once more at the actual send boundary,
    // with no intervening await that could rebind this prepared request to changed authority.
    const fence = requestFences.get(request);
    if (!fence) throw new DOMException("Request authority boundary is missing.", "AbortError");
    fence();
    return globalThis.fetch(request);
  },
});

apiClient.use(authMiddleware);
